import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { HistoryContext, type HistoryContextValue } from './HistoryContext';
import type { HistoryItem } from '../api/types';

const HISTORY_STORAGE_KEY = 'pev.validationHistory.v1';
const MAX_HISTORY_ITEMS = 100;

interface HistoryProviderProps {
  children: ReactNode;
  persistenceEnabled: boolean;
}

type StoredHistoryItem = Omit<HistoryItem, 'timestamp'> & { timestamp: string };

function isStoredHistoryItem(value: unknown): value is StoredHistoryItem {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const item = value as Record<string, unknown>;
  return (
    typeof item.id === 'string' &&
    typeof item.email === 'string' &&
    typeof item.timestamp === 'string' &&
    typeof item.result === 'object' &&
    item.result !== null
  );
}

function toHistoryItem(item: StoredHistoryItem): HistoryItem | null {
  const timestamp = new Date(item.timestamp);
  if (Number.isNaN(timestamp.getTime())) {
    return null;
  }

  return {
    ...item,
    timestamp,
  };
}

function readPersistedHistory(): HistoryItem[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter(isStoredHistoryItem)
      .map(toHistoryItem)
      .filter((item): item is HistoryItem => item !== null)
      .slice(0, MAX_HISTORY_ITEMS);
  } catch {
    return [];
  }
}

function writePersistedHistory(history: HistoryItem[]): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const serializable = history.slice(0, MAX_HISTORY_ITEMS).map((item) => ({
      ...item,
      timestamp: item.timestamp.toISOString(),
    }));
    window.localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(serializable));
  } catch {
    // Ignore storage failures and preserve in-memory behavior.
  }
}

export function HistoryProvider({
  children,
  persistenceEnabled,
}: HistoryProviderProps) {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const hasHydratedFromStorage = useRef(false);

  useEffect(() => {
    if (!persistenceEnabled) {
      hasHydratedFromStorage.current = false;
      return;
    }

    if (hasHydratedFromStorage.current) {
      return;
    }

    const persistedHistory = readPersistedHistory();
    if (persistedHistory.length > 0) {
      setHistory((previous) => {
        const mergedHistory = [...previous, ...persistedHistory];
        return mergedHistory.slice(0, MAX_HISTORY_ITEMS);
      });
    }

    hasHydratedFromStorage.current = true;
  }, [persistenceEnabled]);

  useEffect(() => {
    if (!persistenceEnabled) {
      return;
    }

    writePersistedHistory(history);
  }, [history, persistenceEnabled]);

  const addToHistory = useCallback((item: Omit<HistoryItem, 'id' | 'timestamp'>) => {
    const newItem: HistoryItem = {
      ...item,
      id: crypto.randomUUID(),
      timestamp: new Date(),
    };
    setHistory((prev) => [newItem, ...prev].slice(0, MAX_HISTORY_ITEMS));
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  const removeFromHistory = useCallback((id: string) => {
    setHistory((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const value: HistoryContextValue = {
    history,
    persistenceEnabled,
    addToHistory,
    clearHistory,
    removeFromHistory,
  };

  // React 19 new syntax: <Context> instead of <Context.Provider>
  return <HistoryContext value={value}>{children}</HistoryContext>;
}
